"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import {
  Activity,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Building2,
  Check,
  Code2,
  Landmark,
  Lock,
  PiggyBank,
  Plus,
  RefreshCw,
  Rocket,
  ShieldCheck,
  ShoppingBag,
  TrendingUp,
  Truck,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ── Data ────────────────────────────────────────────────────────────────────

const PARTNERSHIPS_EMAIL = "zbaig@justgold.app";
const PARTNERSHIPS_MAILTO = `mailto:${PARTNERSHIPS_EMAIL}?subject=${encodeURIComponent(
  "Partnership enquiry"
)}`;

type IconItem = {
  title: string;
  description: string;
  icon: LucideIcon;
  typicalUse?: string;
};

const PLATFORMS: IconItem[] = [
  {
    title: "Banks & neobanks",
    description:
      "A Shariah-compliant savings product that sits alongside deposits without competing with them. Customers who would not open a fixed deposit will set aside AED 100 a month in gold.",
    typicalUse:
      "Typical use: goal-based savings accounts, Ramadan and wedding campaigns",
    icon: Building2,
  },
  {
    title: "Wallets & super-apps",
    description:
      "A reason for customers to keep a balance with you rather than move it out. Gold gives idle money somewhere to go, inside your app, and gives you a new revenue line on money already sitting there.",
    typicalUse:
      "Typical use: round-ups, spare change into gold, in-app savings pots",
    icon: Wallet,
  },
  {
    title: "E-commerce, loyalty & remittance",
    description:
      "Gold as a reward that holds its value, or as something to send home instead of cash. Grams do not expire the way points do, and recipients can take delivery as a coin.",
    typicalUse: "Typical use: cashback in gold, gifting, remittance top-ups",
    icon: ShoppingBag,
  },
];

const WHAT_YOU_GET: IconItem[] = [
  {
    title: "API & SDKs",
    description:
      "REST API with iOS and Android SDKs. Sandbox, documentation and a named integration engineer from kick-off.",
    icon: Code2,
  },
  {
    title: "Live pricing",
    description: "Buy and sell rates streamed to your app, updated in real time.",
    icon: Activity,
  },
  {
    title: "Custody by Loomis",
    description:
      "Allocated, insured storage in Dubai with independent audit. Held separately from company assets.",
    icon: Lock,
  },
  {
    title: "Shariah governance",
    description:
      "Certified by Panacea Consulting, Bahrain. Your compliance team gets the certificate and the structure documentation.",
    icon: BadgeCheck,
  },
  {
    title: "Redemption & delivery",
    description:
      "Coins and bars, authenticated and delivered insured across the UAE. We handle it end to end.",
    icon: Truck,
  },
  {
    title: "Partner dashboard",
    description:
      "Volumes, holdings, redemption activity and revenue in one place. Exportable, with reconciliation reporting.",
    icon: BarChart3,
  },
  {
    title: "Goal-based savings",
    description:
      "Recurring savings towards a wedding, Hajj, a child or a safety net. The goals your customers already save for, built in.",
    icon: RefreshCw,
  },
  {
    title: "Security",
    description:
      "ISO 27001:2022 certified. AES-256 encryption, audit trails on every gram, and traceability from purchase to delivery.",
    icon: ShieldCheck,
  },
  {
    title: "White-labelling",
    description:
      "Your brand, your design system, your tone. Customers never see ours unless you want them to.",
    icon: Landmark,
  },
];

const STEPS = [
  {
    number: "01",
    label: "You",
    title: "Integrate",
    description:
      "REST API and mobile SDKs for iOS and Android. Sandbox access on day one, live pricing, buy, sell, holdings, and redemption endpoints. Most partners are through integration in two to three weeks.",
  },
  {
    number: "02",
    label: "Us",
    title: "We handle compliance and custody",
    description:
      "Metal sourcing, allocation, insured vaulting with Loomis International, independent audit, and Shariah governance. Your customers' gold is allocated to them and held separately from company assets.",
  },
  {
    number: "03",
    label: "Us",
    title: "We run fulfilment",
    description:
      "Minting, authentication, insured door-to-door delivery across the UAE. Your support team never touches a physical shipment.",
  },
  {
    number: "04",
    label: "You",
    title: "You keep the relationship",
    description:
      "Your brand throughout, your customer data, your product decisions. A partner dashboard gives you volumes, holdings, and revenue in one place.",
  },
];

const PARTNERS = [
  {
    name: "AbhiPay",
    logo: "/partner-logo-abhi.png",
    // The mark's own background tone, so each logo sits on a tile that suits it.
    tile: "bg-white",
  },
  {
    name: "Du Pay",
    detail: "Micro Investment",
    logo: "/partner-logo-dupay.png",
    tile: "bg-white",
  },
  {
    name: "Cusp",
    detail: "Denara Group",
    logo: "/partner-logo-cusp.png",
    tile: "bg-[#1a110c]",
  },
];

const COMPLIANCE_FAQS = [
  {
    question: "Who actually holds the gold?",
    answer:
      "Loomis International, in an insured vault in Dubai. Metal is allocated to the end customer, held separately from JustGold's own assets, and independently audited. Your customers own gold, not a claim against our balance sheet.",
  },
  {
    question: "Is JustGold regulated?",
    answer:
      "JustGold operates under UAE financial regulations and is ISO 27001 certified. We can share our regulatory registrations, audit reports, and security documentation under NDA during due diligence.",
  },
  {
    question: "Who is the Shariah certification from?",
    answer:
      "JustGold is independently certified by Panacea Consulting, Bahrain. Every purchase is real physical gold, handled under recognised Shariah principles with no interest.",
  },
  {
    question: "What happens to customer gold if JustGold stops trading?",
    answer:
      "Because metal is allocated to each customer and held by Loomis, separately from our own assets, it does not form part of JustGold's estate. Customers keep title to their gold and can take delivery or transfer it regardless of our status.",
  },
  {
    question: "Who owns the customer relationship and the data?",
    answer:
      "You do. It is your brand, your app, and your customer. JustGold runs as infrastructure behind the scenes, processes only what is needed to hold and move gold, and does not market to your users.",
  },
  {
    question: "How long does integration take?",
    answer:
      "Most partners are live in two to three weeks with the API or SDK, with sandbox access from day one and support from our engineering team throughout.",
  },
  {
    question: "What do you need from us to start?",
    answer:
      "A short discovery call, your use case, and standard onboarding and compliance documents. We share API keys and sandbox access so your team can start building right away.",
  },
];

const COMMERCIALS = [
  {
    label: "To integrate",
    title: "No licence fee",
    description:
      "No setup charge and no minimum volume commitment. You carry your own engineering time, which for most partners is two to three weeks.",
    chip: "Sandbox access is free from day one",
    icon: Rocket,
  },
  {
    label: "To run",
    title: "No platform fee",
    description:
      "Custody, insurance, auditing, and Shariah governance are covered by us. You are not paying to keep gold sitting in a vault.",
    chip: "Storage is free for as long as customers hold",
    icon: ShieldCheck,
  },
  {
    label: "How we both earn",
    title: "Revenue share",
    description:
      "Our margin sits in the buy and sell spread. You take an agreed share of it on every transaction your customers make. Physical delivery is charged separately and shown to the customer before they confirm.",
    chip: "Rates agreed per partner, based on volume",
    icon: TrendingUp,
    featured: true,
  },
];

// ── Motion ──────────────────────────────────────────────────────────────────

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Shared bits ─────────────────────────────────────────────────────────────

function IconChip({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span
      className="flex h-12 w-12 items-center justify-center rounded-card-sm bg-primary-50 text-primary-600 transition-colors duration-300 group-hover:bg-primary-100"
      aria-hidden="true"
    >
      <Icon className="h-5 w-5" strokeWidth={2} />
    </span>
  );
}

function SectionHeading({
  id,
  eyebrow,
  title,
  description,
  centered = false,
}: {
  id: string;
  eyebrow: string;
  title: React.ReactNode;
  description?: string;
  centered?: boolean;
}) {
  return (
    <div className={cn("max-w-[720px]", centered && "mx-auto text-center")}>
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500">
        {eyebrow}
      </p>
      <h2
        id={id}
        className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
      >
        {title}
      </h2>
      {description ? (
        <p className="mt-4 text-base leading-relaxed text-text-body sm:text-lg">
          {description}
        </p>
      ) : null}
    </div>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────────

function PartnerHero({ reduce }: { reduce: boolean }) {
  return (
    <section
      className="relative w-full overflow-hidden bg-neutral-0"
      aria-labelledby="partner-hero-heading"
    >
      {/* Decorative background image, faint */}
      <Image
        src="/hero-bg.png"
        alt=""
        width={1975}
        height={796}
        priority
        className="pointer-events-none absolute inset-x-0 top-0 h-full w-full select-none object-cover object-top opacity-40"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,rgba(14,58,138,0.10),transparent_60%)]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[120px] bg-gradient-to-b from-transparent to-neutral-0"
        aria-hidden="true"
      />

      <motion.div
        variants={container}
        initial="hidden"
        animate="visible"
        className="relative z-10 mx-auto grid max-w-[1200px] items-center gap-12 px-6 pb-16 pt-16 sm:px-10 sm:pb-20 sm:pt-20 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:pb-24 lg:pt-24"
      >
        <div>
          <motion.p
            variants={fadeUp(reduce)}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary-100 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500 backdrop-blur-sm"
          >
            Gold as a Service
          </motion.p>

          <motion.h1
            id="partner-hero-heading"
            variants={fadeUp(reduce)}
            className="text-[clamp(2.15rem,4.6vw,3.5rem)] font-semibold leading-[1.08] tracking-[-0.015em] text-primary-800"
          >
            Your customers want to save in gold.
          </motion.h1>

          <motion.p
            variants={fadeUp(reduce)}
            className="mt-6 max-w-[560px] text-lg leading-relaxed text-text-body"
          >
            Give them a way to do it without leaving your app. We handle the
            metal, the vault, the compliance and the delivery. You keep the
            customer relationship, and a share of every transaction.
          </motion.p>

          <motion.div
            variants={fadeUp(reduce)}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <Link
              href={PARTNERSHIPS_MAILTO}
              className={cn(buttonVariants({ variant: "accent", size: "lg" }))}
            >
              Talk to our partnerships team
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              href="#partner-how-heading"
              className={cn(
                buttonVariants({ variant: "outline-brand", size: "lg" })
              )}
            >
              See how it works
            </Link>
          </motion.div>
        </div>

        {/* Product visual */}
        <motion.div variants={fadeUp(reduce)} className="relative">
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-card border border-[#e3ecfb] bg-gradient-to-b from-[#fafbff] to-[#eef3fb]">
            <Image
              src="/partner-hero-diagram.jpg"
              alt="Diagram showing your app connecting through the JustGold API to JustGold infrastructure and Loomis vaulted physical gold in Dubai"
              fill
              sizes="(max-width: 1024px) 90vw, 560px"
              className="object-contain p-4"
              priority
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ── Platforms ───────────────────────────────────────────────────────────────

function PartnerPlatforms({ reduce }: { reduce: boolean }) {
  return (
    <motion.section
      className="bg-neutral-0 pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="partner-platforms-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={container}
    >
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-8">
        <motion.div variants={fadeUp(reduce)} className="mb-12">
          <SectionHeading
            id="partner-platforms-heading"
            eyebrow="Who this is for"
            title="Three ways partners work with us"
          />
        </motion.div>

        <motion.div
          variants={container}
          className="grid gap-6 md:grid-cols-3 md:gap-7"
        >
          {PLATFORMS.map(({ title, description, icon, typicalUse }) => (
            <motion.article
              key={title}
              variants={fadeUp(reduce)}
              className="group flex flex-col rounded-card border border-[#e3ecfb] bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-[0_24px_50px_-28px_rgba(10,45,110,0.28)] sm:p-9"
            >
              <IconChip icon={icon} />
              <h3 className="mt-5 text-xl font-bold leading-[1.25] tracking-[-0.01em] text-text-heading">
                {title}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-text-body">
                {description}
              </p>
              {typicalUse && (
                <p className="mt-4 border-t border-[#e3ecfb] pt-4 text-sm leading-relaxed text-text-muted">
                  {typicalUse}
                </p>
              )}
            </motion.article>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}

// ── What you get ─────────────────────────────────────────────────────────────

function PartnerWhatYouGet({ reduce }: { reduce: boolean }) {
  return (
    <motion.section
      className="bg-primary-900 pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="partner-offerings-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.12 }}
      variants={container}
    >
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-8">
        <motion.div variants={fadeUp(reduce)} className="mb-12 max-w-[720px]">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-accent-300">
            What you get
          </p>
          <h2
            id="partner-offerings-heading"
            className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-white"
          >
            Everything needed to launch
          </h2>
          <p className="mt-4 text-base leading-relaxed text-primary-200 sm:text-lg">
            Modular, so you can start with buy and sell and add redemption
            later.
          </p>
        </motion.div>

        <motion.ul
          variants={container}
          className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        >
          {WHAT_YOU_GET.map(({ title, description, icon: Icon }) => (
            <motion.li
              key={title}
              variants={fadeUp(reduce)}
              className="rounded-card border border-white/10 bg-white/5 p-6"
            >
              <Icon
                className="h-5 w-5 text-accent-300"
                strokeWidth={1.8}
                aria-hidden="true"
              />
              <h3 className="mt-4 text-base font-bold leading-[1.25] tracking-[-0.01em] text-white">
                {title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-primary-200">
                {description}
              </p>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </motion.section>
  );
}

// ── How it works ────────────────────────────────────────────────────────────

function PartnerHowItWorks({ reduce }: { reduce: boolean }) {
  return (
    <motion.section
      className="bg-neutral-0 pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="partner-how-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={container}
    >
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-8">
        <motion.div variants={fadeUp(reduce)} className="mb-12">
          <SectionHeading
            id="partner-how-heading"
            eyebrow="How it works"
            title="Four steps, and we own two of them"
            description="Your engineering effort stops at the API call. Everything downstream of that is ours."
          />
        </motion.div>

        <motion.div
          variants={container}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          {STEPS.map(({ number, label, title, description }) => {
            const isUs = label === "Us";

            return (
              <motion.article
                key={number}
                variants={fadeUp(reduce)}
                className={cn(
                  "flex flex-col rounded-card border p-6",
                  isUs
                    ? "border-primary-900 bg-primary-900"
                    : "border-[#e3ecfb] bg-[#f7faff]"
                )}
              >
                <span
                  className={cn(
                    "mb-4 inline-flex w-fit items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em]",
                    isUs
                      ? "bg-white/10 text-accent-300"
                      : "bg-accent-100 text-accent-700"
                  )}
                >
                  {label}
                  <span className="tabular-nums opacity-70">{number.replace(/^0/, "")}</span>
                </span>
                <h3
                  className={cn(
                    "text-lg font-bold leading-[1.25] tracking-[-0.01em]",
                    isUs ? "text-white" : "text-text-heading"
                  )}
                >
                  {title}
                </h3>
                <p
                  className={cn(
                    "mt-3 text-sm leading-relaxed",
                    isUs ? "text-primary-200" : "text-text-body"
                  )}
                >
                  {description}
                </p>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </motion.section>
  );
}

// ── Existing partners ───────────────────────────────────────────────────────

function PartnerLogos({ reduce }: { reduce: boolean }) {
  return (
    <motion.section
      className="bg-neutral-0 pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="partner-logos-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={container}
    >
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-8">
        <motion.div variants={fadeUp(reduce)} className="mb-10 text-center">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500">
            Existing partners
          </p>
          <h2
            id="partner-logos-heading"
            className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
          >
            Teams already building with JustGold.
          </h2>
        </motion.div>

        <motion.ul
          variants={container}
          className="grid gap-6 sm:grid-cols-3 md:gap-7"
        >
          {PARTNERS.map(({ name, detail, logo, tile }) => (
            <motion.li
              key={name}
              variants={fadeUp(reduce)}
              className="flex flex-col items-center gap-3 text-center"
            >
              <span
                className={cn(
                  "flex h-28 w-full items-center justify-center rounded-card border border-[#e3ecfb] px-6 sm:px-8",
                  tile
                )}
              >
                <Image
                  src={logo}
                  alt={detail ? `${name}, ${detail}` : name}
                  width={280}
                  height={112}
                  className="max-h-16 w-auto max-w-[80%] object-contain"
                />
              </span>
              <span className="text-sm font-medium text-primary-900">
                {name}
                {detail ? (
                  <span className="font-normal text-text-muted"> · {detail}</span>
                ) : null}
              </span>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </motion.section>
  );
}

// ── Commercials ─────────────────────────────────────────────────────────────

function PartnerCommercials({ reduce }: { reduce: boolean }) {
  return (
    <motion.section
      className="bg-[#f7faff] pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="partner-commercials-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.12 }}
      variants={container}
    >
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-8">
        <motion.div variants={fadeUp(reduce)} className="mb-12">
          <SectionHeading
            id="partner-commercials-heading"
            eyebrow="The commercials"
            title="What it costs you to launch."
            description="The honest version, because this is the question that decides whether a conversation goes anywhere."
          />
        </motion.div>

        <motion.div
          variants={container}
          className="grid gap-6 md:grid-cols-3 md:gap-7"
        >
          {COMMERCIALS.map(({ label, title, description, chip, icon: Icon, featured }) => (
            <motion.article
              key={title}
              variants={fadeUp(reduce)}
              className={cn(
                "group relative flex flex-col overflow-hidden rounded-card border bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(10,45,110,0.28)] sm:p-9",
                featured
                  ? "border-primary-200 shadow-[0_18px_40px_-30px_rgba(10,45,110,0.35)]"
                  : "border-[#e3ecfb] hover:border-primary-200"
              )}
            >
              <span
                className="flex h-12 w-12 items-center justify-center rounded-card-sm bg-primary-50 text-primary-600 transition-colors duration-300 group-hover:bg-primary-100"
                aria-hidden="true"
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </span>

              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500">
                {label}
              </p>
              <h3 className="mt-2 text-[clamp(1.35rem,2.2vw,1.65rem)] font-bold leading-[1.2] tracking-[-0.01em] text-text-heading">
                {title}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-text-body">
                {description}
              </p>
              <div className="mt-auto pt-6">
                <p className="flex items-start gap-2 rounded-card-sm bg-primary-50 px-4 py-3 text-sm font-semibold text-primary-700">
                  <Check
                    className="mt-0.5 h-4 w-4 shrink-0 text-primary-600"
                    strokeWidth={2.4}
                    aria-hidden="true"
                  />
                  {chip}
                </p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}

// ── Compliance FAQ ──────────────────────────────────────────────────────────

function PartnerCompliance({ reduce }: { reduce: boolean }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <motion.section
      className="bg-neutral-0 pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="partner-compliance-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.12 }}
      variants={container}
    >
      <div className="mx-auto max-w-[820px] px-6 sm:px-10 lg:px-8">
        <motion.div variants={fadeUp(reduce)} className="mb-10">
          <SectionHeading
            id="partner-compliance-heading"
            eyebrow="Trust and compliance"
            title="What your compliance team will ask."
            description="We would rather answer these on a public page than three weeks into a due diligence process."
          />
        </motion.div>

        <motion.div variants={fadeUp(reduce)} className="flex flex-col">
          {COMPLIANCE_FAQS.map((faq, i) => {
            const isOpen = openIndex === i;
            const panelId = `partner-faq-panel-${i}`;
            const buttonId = `partner-faq-button-${i}`;

            return (
              <div key={faq.question} className="border-t border-[#e3ecfb] last:border-b">
                <h3>
                  <button
                    id={buttonId}
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="flex w-full items-center justify-between gap-6 py-5 text-left sm:py-6"
                  >
                    <span className="text-base font-bold leading-snug text-primary-900 sm:text-lg">
                      {faq.question}
                    </span>
                    <Plus
                      className={cn(
                        "h-5 w-5 shrink-0 text-primary-600 transition-transform duration-300",
                        isOpen ? "rotate-45" : "rotate-0"
                      )}
                      strokeWidth={2.2}
                      aria-hidden="true"
                    />
                  </button>
                </h3>

                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <motion.div
                      key="content"
                      id={panelId}
                      role="region"
                      aria-labelledby={buttonId}
                      initial={reduce ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={reduce ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
                      transition={{ duration: 0.28, ease: [0.2, 0, 0, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="pb-6 pr-10 text-base leading-relaxed text-text-body">
                        {faq.answer}
                      </p>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            );
          })}
        </motion.div>
      </div>
    </motion.section>
  );
}

// ── CTA ─────────────────────────────────────────────────────────────────────

function PartnerCTA({ reduce }: { reduce: boolean }) {
  return (
    <motion.section
      className="bg-neutral-0 pb-16 sm:pb-20 lg:pb-24"
      aria-labelledby="partner-cta-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={container}
    >
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-8">
        <motion.div
          variants={fadeUp(reduce)}
          className="relative isolate flex flex-col items-start justify-between gap-8 overflow-hidden rounded-card p-8 shadow-[0_30px_80px_-28px_rgba(4,15,40,0.45)] sm:p-12 lg:flex-row lg:items-center lg:p-16"
        >
          {/* Same dark glow band used by the home and other pages. */}
          <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
            <Image
              src="/cta-glow-bg.png"
              alt=""
              fill
              sizes="1200px"
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 via-primary-900/70 to-primary-900/40" />
          </div>

          <div>
            <h2
              id="partner-cta-heading"
              className="max-w-[620px] text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-white"
            >
              Let&apos;s talk about what your customers would actually use.
            </h2>
            <p className="mt-4 max-w-[540px] text-base leading-relaxed text-primary-200 sm:text-lg">
              No pitch deck required. Tell us what your product looks like and
              we will tell you honestly whether gold fits, and what it would
              take to launch.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                href={PARTNERSHIPS_MAILTO}
                className={cn(buttonVariants({ variant: "accent", size: "md" }))}
              >
                Talk to our partnerships team
              </Link>
              <Link
                href="/licenses"
                className={cn(
                  "inline-flex items-center justify-center gap-2 rounded-lg border border-white/30 px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10"
                )}
              >
                Read our licences &amp; custody
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function PartnerPage() {
  const reduce = useReducedMotion() ?? false;

  return (
    <>
      <PartnerHero reduce={reduce} />
      {/* Existing partners section temporarily disabled — urgent request */}
      <PartnerPlatforms reduce={reduce} />
      <PartnerHowItWorks reduce={reduce} />
      <PartnerCommercials reduce={reduce} />
      <PartnerCompliance reduce={reduce} />
      <PartnerWhatYouGet reduce={reduce} />
      <PartnerCTA reduce={reduce} />
    </>
  );
}
